import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ApiError } from '@/lib/api'
import type { ReferrerRow } from '@/hooks/chain/useReferralRoster'
import { ReferrerRosterRow, type ReferrerActionKind } from './ReferrerRosterRow'

export interface ReferrerRosterProps {
    referrers: ReferrerRow[]
    isLoading: boolean
    error: unknown
    /** Disables the row lifecycle buttons (mutation in flight or missing OPERATOR_ROLE). */
    actionsDisabled: boolean
    onAction: (kind: ReferrerActionKind, row: ReferrerRow) => void
    onRetry: () => void
}

function errorCopy(error: unknown): { title: string; detail: string; retry: boolean } {
    if (error instanceof ApiError && error.status === 401) {
        return {
            title: 'Your session has expired.',
            detail: 'Reload the page and sign in with your wallet again to see the roster.',
            retry: false,
        }
    }
    if (error instanceof ApiError && error.status === 403) {
        return {
            title: 'Your console role cannot view referrers.',
            detail: 'Ask a console admin for the admin, MLRO or read-only role on this chain.',
            retry: false,
        }
    }
    return {
        title: 'Could not load the referrer roster.',
        detail: `${error instanceof Error ? error.message : 'Unknown error'}. Try again; if it keeps failing, check the service status on the dashboard.`,
        retry: true,
    }
}

// The referrer roster card: the referrer set comes from blockpot-service
// (indexed ReferrerRegistered logs), each row's status and balances from live
// chain reads. Every state says what it is and what to do next.
export function ReferrerRoster({
    referrers,
    isLoading,
    error,
    actionsDisabled,
    onAction,
    onRetry,
}: ReferrerRosterProps) {
    return (
        <Card>
            <CardHeader>
                <CardTitle>Referrer roster</CardTitle>
                <CardDescription>
                    Registered referrers, indexed by the Blockpot service. Statuses and balances are
                    read live from the chain.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                {isLoading ? (
                    <div className="space-y-2" aria-busy="true">
                        <p className="text-muted-foreground text-sm">Loading referrers…</p>
                        <Skeleton className="h-24 w-full" />
                    </div>
                ) : error ? (
                    <ErrorState error={error} onRetry={onRetry} />
                ) : referrers.length === 0 ? (
                    <div className="space-y-1">
                        <p className="text-sm font-medium">No referrers yet</p>
                        <p className="text-muted-foreground text-sm">
                            Register a referrer above to give them a code. New registrations appear
                            here shortly after they confirm on-chain.
                        </p>
                    </div>
                ) : (
                    referrers.map((row) => (
                        <ReferrerRosterRow
                            key={row.address}
                            row={row}
                            actionsDisabled={actionsDisabled}
                            onAction={(kind) => onAction(kind, row)}
                        />
                    ))
                )}
            </CardContent>
        </Card>
    )
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
    const copy = errorCopy(error)
    return (
        <div role="alert" className="space-y-2">
            <p className="text-sm font-medium">{copy.title}</p>
            <p className="text-muted-foreground text-sm">{copy.detail}</p>
            {copy.retry && (
                <Button variant="outline" size="sm" onClick={onRetry}>
                    Retry loading referrers
                </Button>
            )}
        </div>
    )
}
